// Pointy-top hex layout: converts between axial (q,r) and screen (x,y).
// Grid uses offset (odd-r) for a rectangular 7x5 shape but stores everything in axial.

import { Hex, type HexCoord } from "./HexCoord";

export interface Point {
  x: number;
  y: number;
}

export interface HexLayoutOptions {
  size: number; // radius (center to corner)
  origin: Point; // screen origin for (q=0,r=0)
}

export class HexLayout {
  readonly size: number;
  readonly origin: Point;
  readonly width: number; // sqrt(3) * size
  readonly height: number; // 2 * size
  readonly horizSpacing: number; // width
  readonly vertSpacing: number; // 3/4 * height

  constructor(options: HexLayoutOptions) {
    this.size = options.size;
    this.origin = options.origin;
    this.width = Math.sqrt(3) * options.size;
    this.height = 2 * options.size;
    this.horizSpacing = this.width;
    this.vertSpacing = (3 / 4) * this.height;
  }

  hexToPixel(h: HexCoord): Point {
    const x = this.size * Math.sqrt(3) * (h.q + h.r / 2) + this.origin.x;
    const y = this.size * (3 / 2) * h.r + this.origin.y;
    return { x, y };
  }

  pixelToHex(p: Point): HexCoord {
    const px = p.x - this.origin.x;
    const py = p.y - this.origin.y;
    const q = ((Math.sqrt(3) / 3) * px - (1 / 3) * py) / this.size;
    const r = ((2 / 3) * py) / this.size;
    return this.axialRound(q, r);
  }

  private axialRound(q: number, r: number): HexCoord {
    const x = q;
    const z = r;
    const y = -x - z;
    let rx = Math.round(x);
    let ry = Math.round(y);
    let rz = Math.round(z);
    const xd = Math.abs(rx - x);
    const yd = Math.abs(ry - y);
    const zd = Math.abs(rz - z);
    if (xd > yd && xd > zd) rx = -ry - rz;
    else if (yd > zd) ry = -rx - rz;
    else rz = -rx - ry;
    return Hex(rx, rz);
  }

  // Corner points of a hex for drawing.
  hexCorners(h: HexCoord): Point[] {
    const center = this.hexToPixel(h);
    const corners: Point[] = [];
    for (let i = 0; i < 6; i++) {
      // Pointy-top: first corner at top (angle = -90deg + i*60deg)
      const angle = (Math.PI / 180) * (60 * i - 90);
      corners.push({
        x: center.x + this.size * Math.cos(angle),
        y: center.y + this.size * Math.sin(angle),
      });
    }
    return corners;
  }
}
